import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Calendar, Clock, Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { JsonLd, schemas } from "../components/Seo";
import { api, fileUrl, STORE_LABEL, priceFmt, dealTitle } from "../lib/api";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function readingMinutes(markdown) {
  if (!markdown) return 1;
  const words = markdown.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 220)); // average italian reader ~220 wpm
}

export default function BlogPost() {
  const { slug } = useParams();

  const { data: article, isLoading, isError } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => api.get(`/articles/${slug}`).then((r) => r.data),
    retry: false,
  });

  const { data: allList } = useQuery({
    queryKey: ["articles"],
    queryFn: () => api.get("/articles").then((r) => r.data.items),
    staleTime: 5 * 60_000,
  });
  const related = (allList || []).filter((a) => a.slug !== slug).slice(0, 3);

  // Internal linking: pull a wider pool of recent deals for cross-linking blog -> product
  const { data: dealsList } = useQuery({
    queryKey: ["blog-related-deals"],
    queryFn: () => api.get("/deals", { params: { limit: 100 } }).then((r) => r.data.items || []),
    staleTime: 5 * 60_000,
  });

  const matchedDeals = React.useMemo(() => {
    if (!dealsList || !article) return [];

    // Italian + generic stopwords to ignore when extracting keywords
    const STOP = new Set([
      "lego","sets","set","cosa","come","sono","essere","molto","molti","molte",
      "anche","altro","altri","altre","oltre","tutti","tutte","ogni","quando",
      "quale","quali","quanto","quanta","questo","questa","questi","queste",
      "quello","quella","quegli","quelle","perche","perché","quindi","invece",
      "senza","sopra","sotto","prima","dopo","ancora","sempre","mai","poi",
      "loro","nostro","vostro","mio","tuo","suo","della","dello","delle","degli",
      "nella","nello","nelle","negli","sulla","sullo","sulle","sugli","alla",
      "allo","alle","agli","dalla","dallo","dalle","dagli","fino","oppure",
      "vero","puoi","potete","posso","essere","sarà","siano","stato","stata",
      "stati","state","fare","fanno","viene","vengono","venuto","venuti",
      "guida","articolo","blog","versione","modello","modelli","prezzo","prezzi",
      "sconto","sconti","offerta","offerte","store","amazon","ebay","aliexpress",
      "costruzioni","costruzione","mattoncini","mattoncino","pezzi","pezzo",
      "anni","anno","euro","gioco","giochi","with","that","this","from","have",
      "more","than","what","when","where","which","while","they","their","there",
      "adulto","adulti","bambino","bambini","ragazzo","ragazzi","ragazza","ragazze",
      "esposizione","decorazione","decorativo","collezione","collezionisti",
      "edizione","collector","display","figure","minifigure","minifigures",
      "costruire","montare","montaggio","kit","modellino","modellini","detto",
      "fatto","dato","cose","cosa","tipo","grande","piccolo","piccola","grandi",
      "iconico","iconica","iconici","iconiche","classico","classica","classici","classiche",
      "nuovo","nuova","nuovi","nuove","vecchio","vecchia","vecchi","vecchie",
      "ufficiale","ufficiali","disponibile","disponibili","trovare","trovi",
      "modellismo","action","figure","robot","robotica","kit","scala","scale",
      "luce","luci","luminoso","luminosa","luminosi","gigante","giganti","mini",
      "creativo","creativa","creativi","creative","divertente","divertenti",
      "esperienza","esperienze","passione","passioni","appassionati","appassionato",
      "regalo","regali","souvenir","perfetto","perfetta","perfetti","perfette",
      "ideale","ideali","completo","completa","completi","complete","unico","unica",
      "particolare","particolari","speciale","speciali","esclusivo","esclusiva",
      "premium","standard","base","basico","semplice","semplici","complesso","complessi",
      "primo","prima","secondo","seconda","terzo","terza","quarto","quarta",
    ]);

    // Build full corpus from the article — title + excerpt + actual body markdown
    const corpus = [
      article.title || "",
      article.excerpt || "",
      article.content_markdown || "",
    ]
      .join(" ")
      .toLowerCase()
      .replace(/[#*_`>\[\]()\-]/g, " ")
      .replace(/[^\p{L}\p{N}\s]/gu, " ");

    const freq = new Map();
    for (const raw of corpus.split(/\s+/)) {
      const w = raw.trim();
      if (w.length < 4) continue;
      if (STOP.has(w)) continue;
      if (/^\d+$/.test(w)) continue;
      freq.set(w, (freq.get(w) || 0) + 1);
    }

    // Document frequency across the deals pool — terms that appear in many deals
    // are too generic (e.g. "modello", "set"). Drop them.
    const totalDeals = dealsList.length || 1;
    const tooCommon = new Set();
    for (const w of freq.keys()) {
      let df = 0;
      for (const d of dealsList) {
        const hay = ((d.title || "") + " " + (d.category || "")).toLowerCase();
        if (hay.includes(w)) df += 1;
      }
      // If a term appears in >35% of deals OR in fewer than 1 deal we drop it
      if (df === 0 || df / totalDeals > 0.35) tooCommon.add(w);
    }

    const keywords = [...freq.entries()]
      .filter(([w]) => !tooCommon.has(w))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 25)
      .map(([w, c]) => ({ w, weight: 1 + Math.log(1 + c) }));

    if (keywords.length === 0) return [];

    // Score each deal — require at least 2 distinct keyword hits OR one strong hit
    const scored = dealsList
      .map((d) => {
        const hay = ((d.title || "") + " " + (d.category || "")).toLowerCase();
        let score = 0;
        let hits = 0;
        let strong = false;
        for (const { w, weight } of keywords) {
          if (hay.includes(w)) {
            score += weight;
            hits += 1;
            if (weight >= 1.7) strong = true; // word repeated ≥3 times in article
          }
        }
        return { d, score, hits, strong };
      })
      .filter((x) => x.strong || x.hits >= 2)
      .sort((a, b) => b.score - a.score);

    // STRICT: no fallback. If no relevant deals, hide the section.
    return scored.slice(0, 4).map((x) => x.d);
  }, [dealsList, article]);

  if (!isLoading && isError) {
    return <Navigate to="/guide" replace />;
  }

  const SITE = "https://dailybrickdeals.com";

  const articleSchema = article
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt || "",
        image: article.cover_image || `${SITE}/logo.png`,
        datePublished: article.created_at,
        dateModified: article.updated_at || article.created_at,
        author: { "@type": "Organization", name: "BrickDailyDeals" },
        publisher: {
          "@type": "Organization",
          name: "BrickDailyDeals",
          logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
        },
        mainEntityOfPage: `${SITE}/guide/${slug}`,
      }
    : null;

  return (
    <div className="min-h-screen bg-[var(--bdd-bg)]" data-testid="page-blog-post">
      {article && (
        <>
          <Seo
            title={article.title}
            description={article.excerpt || article.title}
            path={`/guide/${slug}`}
            image={article.cover_image || `${SITE}/logo.png`}
            type="article"
          />
          <JsonLd data={articleSchema} />
          <JsonLd
            data={schemas.breadcrumb([
              { name: "Home", url: "/" },
              { name: "Guide e News", url: "/guide" },
              { name: article.title, url: `/guide/${slug}` },
            ])}
          />
        </>
      )}
      <Header />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/guide"
          data-testid="blog-back"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8"
        >
          <ArrowLeft size={14} /> Tutte le guide
        </Link>

        {isLoading && (
          <div className="space-y-4">
            <div className="h-10 rounded shimmer" />
            <div className="h-4 rounded shimmer w-2/3" />
            <div className="h-64 rounded shimmer mt-8" />
          </div>
        )}

        {article && (
          <>
            <div className="font-mono-bdd text-xs uppercase tracking-[0.3em] text-[#FBD51C] mb-3">
              Guida
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-zinc-500 mb-8 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={14} />
                <time dateTime={article.created_at}>
                  {formatDate(article.created_at)}
                </time>
              </span>
              <span className="text-zinc-700">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} />
                {readingMinutes(article.content_markdown)} min di lettura
              </span>
              {article.updated_at && article.updated_at !== article.created_at && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="text-zinc-400">
                    Aggiornato il {formatDate(article.updated_at)}
                  </span>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 mb-8 p-3 bg-zinc-900/50 border border-zinc-800/60 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FBD51C] to-[#E71B1F] flex items-center justify-center text-black font-black text-sm shrink-0">
                BD
              </div>
              <div className="text-sm">
                <div className="text-white font-bold">Redazione BrickDailyDeals</div>
                <div className="text-zinc-500 text-xs">
                  Scriviamo guide e recensioni sui set di costruzioni dal 2026
                </div>
              </div>
            </div>

            {article.cover_image && (
              <img
                src={article.cover_image}
                alt={article.title}
                className="w-full aspect-video object-cover rounded-2xl mb-10"
              />
            )}

            <div className="prose prose-invert prose-zinc max-w-none
              prose-headings:font-display prose-headings:text-white
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-zinc-300 prose-p:leading-relaxed
              prose-strong:text-white
              prose-a:text-[#FBD51C] prose-a:no-underline hover:prose-a:underline
              prose-ul:text-zinc-300 prose-li:my-1
              prose-img:rounded-xl"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {article.content_markdown || ""}
              </ReactMarkdown>
            </div>

            {matchedDeals.length > 0 && (
              <section className="mt-12 pt-8 border-t border-zinc-900" data-testid="blog-related-deals">
                <div className="font-mono-bdd text-[11px] uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
                  Offerte in evidenza
                </div>
                <h2 className="font-display font-black text-2xl text-white mb-5">
                  Set di cui parla l&apos;articolo
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {matchedDeals.map((d) => {
                    const dTitle = dealTitle(d);
                    return (
                    <Link
                      key={d.id}
                      to={`/offerta/${d.id}`}
                      data-testid={`blog-deal-${d.id}`}
                      className="group flex gap-4 p-3 rounded-2xl bg-zinc-900/70 border border-zinc-800 hover:border-[#FBD51C]/40 hover:bg-zinc-900 transition-colors"
                    >
                      <div className="w-24 h-24 rounded-xl overflow-hidden bg-zinc-950 border border-zinc-800 shrink-0">
                        {d.image_url ? (
                          <img
                            src={fileUrl(d.image_url)}
                            alt={dTitle}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono-bdd text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                          {STORE_LABEL[d.store] || d.store}
                        </div>
                        <div className="font-display font-bold text-sm text-white leading-snug line-clamp-2 group-hover:text-[#FBD51C] transition-colors">
                          {dTitle}
                        </div>
                        <div className="mt-2 flex items-baseline gap-2">
                          {d.price_current ? (
                            <span className="font-display font-black text-base text-gold-grad">
                              {priceFmt(d.price_current)}
                            </span>
                          ) : null}
                          {d.discount_percent ? (
                            <span className="text-[10px] font-bold text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded">
                              -{d.discount_percent}%
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                    );
                  })}
                </div>
              </section>
            )}

            <div className="mt-12 p-6 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#229ED9]/15 flex items-center justify-center shrink-0">
                <Send size={20} className="text-[#229ED9]" />
              </div>
              <div className="flex-1">
                <div className="font-display text-lg text-white mb-1">
                  Non perderti le prossime offerte!
                </div>
                <p className="text-sm text-zinc-400 mb-3">
                  Iscriviti al canale Telegram per ricevere le offerte sui set
                  di costruzioni in tempo reale.
                </p>
                <a
                  href="https://t.me/BrickDailyDeals"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#229ED9] hover:bg-[#1e8fc4] text-white font-bold text-sm px-4 py-2 rounded-lg"
                >
                  <Send size={14} /> Iscriviti al canale
                </a>
              </div>
            </div>

            {related.length > 0 && (
              <section className="mt-16 pt-10 border-t border-zinc-900">
                <h2 className="font-display text-xl text-white mb-5">
                  Continua a leggere
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {related.map((a) => (
                    <Link
                      key={a.id}
                      to={`/guide/${a.slug}`}
                      data-testid={`related-article-${a.slug}`}
                      className="block bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-[#FBD51C]/40 rounded-xl p-4 transition-colors"
                    >
                      <div className="font-bold text-white text-sm mb-2 line-clamp-2">
                        {a.title}
                      </div>
                      {a.excerpt && (
                        <div className="text-xs text-zinc-400 line-clamp-2">
                          {a.excerpt}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </article>

      <Footer />
    </div>
  );
}
